import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
  PropsWithChildren,
} from 'react';
import { storage, StorageKey } from '@/services/storage';
import { TimezoneVo } from '@/utils/value-objects';
import { getLocalTimezone } from '@/utils/timezones';
import { Timezone, DEFAULT_TIMEZONE } from '@/entities';

interface ITimezoneContextValue {
  timezone: Timezone,
  onTimezoneChange: (timezone: Timezone) => void,
}

const initialValue: ITimezoneContextValue = {
  timezone: DEFAULT_TIMEZONE,
  onTimezoneChange: () => {},
};

const TimezoneContext = createContext<ITimezoneContextValue>(initialValue);

export const useTimezoneContext = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezoneContext must be used within a TimezoneContextProvider');
  }
  return context;
};

const isTimezone = (value: string): value is Timezone => {
  return TimezoneVo.isValidTimezone(value);
};

const getAutoDetectedTimezone = () => {
  const timezone = getLocalTimezone();
  return isTimezone(timezone) ? timezone : DEFAULT_TIMEZONE;
};

export const TimezoneContextProvider = ({ children }: PropsWithChildren) => {
  const [timezone, setTimezone] = useState<Timezone>(
    () => {
      return storage.getItem<Timezone>(StorageKey.TIMEZONE) || getAutoDetectedTimezone() || DEFAULT_TIMEZONE;
    },
  );

  const handleTimezoneChange = useCallback((nextTimezone: Timezone) => {
    setTimezone(nextTimezone);
  }, [setTimezone]);

  useEffect(() => {
    storage.setItem(StorageKey.TIMEZONE, timezone);
  }, [timezone]);

  const value = useMemo(
    () => {
      return {
        timezone,
        onTimezoneChange: handleTimezoneChange,
      };
    },
    [timezone, setTimezone],
  );

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};
